// DateStrategyModal.tsx

import * as React from "react";
import { Row, Col, DatePicker, Tooltip, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import "./DateStrategy.css";
import { DateStrategy } from "app/shared/model/EditProductOffering";
import { DateStrategyAssign, EditPricelistResponse } from "app/shared/model/EditPricelist";
import DateStrategyContent from "./DateStrategyContent";
import DateHelper, { CompareTypes } from "app/helpers/dateHelper";
import { PricingYearId } from "app/shared/model";
import { getPricingYears, getSelectedPricingYear } from "../store/selectors";
import { CurrentUser } from "app/shared/model/CurrentUser";
import { getCurrentUser } from "app/shared/currentUser";
import { toIso } from "../domain/date/iso";
import { normalizeStrategy } from "../domain/dateStrategy/normalize";
import { cloneDeep } from "app/pages/PricelistNew/components/utils/cloneDeep";
import {
    shiftPickerDateToEarliestAllowed,
    minIsoDate,
    maxIsoDate,
} from "../domain/pricingYear/window";
import {
    getAllowedDateStrategyTypeIds,
    toStrategyTypeSelectOptions,
} from "../domain/dateStrategy/allowed";
import { switchStrategyType } from "../domain/dateStrategy/switchType";
import { isUnsavedPricelistOffering } from "../domain/offering/isUnsavedOffering";
interface DateStrategyModalProps {
    dateStrategyToUpdateCreate?: DateStrategyAssign;
    selectedProduct?: EditPricelistResponse;
    isUnsavedOffering?: boolean;
    createNew: boolean;
    minDateFrom: string;
    maxDateFrom: string;
    allowedStartDate: string;
    allowedEndDate: string;
    onPendingChanges: (changes: boolean) => void;
    onDateStrategyUpdate: (dateStrategy: DateStrategyAssign) => void;
}

interface StateProps {
    pricingYears: PricingYearId[];
    selectedPricingYear?: number;
    user: CurrentUser;
}

interface DispatchProps {}

type InternalProps = StateProps & DispatchProps & DateStrategyModalProps;

const DateStrategyModalView = (props: InternalProps) => <>{DateStrategyModal(props)}</>;
const normalizeDates = normalizeStrategy;

const STRATEGY_TOOLTIPS: Record<string, string> = {
    Recurrence: "Repeats on selected calendar day. E.g. starts every Monday.",
    "Fixed Duration": "Fixed start and fixed end date. Must book the full block.",
    "Offering Dates":
        "Flexible start and end within a date range. Can book any length inside the window.",
    "Specific Start Dates": "Must start on fixed date. End date depends on booked duration.",
};

const hasPendingStrategyData = (strategy?: DateStrategyAssign): boolean => {
    if (!strategy) return false;

    const t = strategy.strategyTypeId;

    if (DateStrategy[t] === "Offering Dates") return true;
    if (DateStrategy[t] === "Recurrence") {
        return Boolean(strategy.recurranceStartDays?.length);
    }
    if (DateStrategy[t] === "Fixed Duration") {
        return Boolean(strategy.fixedDurationDates?.filter((d) => !d.isDeleted).length);
    }
    if (DateStrategy[t] === "Specific Start Dates") {
        return Boolean(strategy.specificStartDates?.filter((d) => !d.isDeleted).length);
    }

    return false;
};

function DateStrategyModal(props: InternalProps) {
    const initialDateStrategyId = props.dateStrategyToUpdateCreate
        ? props.dateStrategyToUpdateCreate.strategyTypeId
        : 1;

    const [selectedDateStrategy, setSelectedDateStrategy] = React.useState<
        DateStrategyAssign | undefined
    >(() =>
        props.dateStrategyToUpdateCreate
            ? normalizeDates(cloneDeep(props.dateStrategyToUpdateCreate))
            : undefined
    );

    const currentStrategyTypeId = selectedDateStrategy?.strategyTypeId ?? initialDateStrategyId;
    const selectedDateStrategyType = React.useMemo(
        () => DateStrategy[currentStrategyTypeId],
        [currentStrategyTypeId]
    );

    const isUnsavedOffering =
        props.isUnsavedOffering === true ||
        isUnsavedPricelistOffering(props.selectedProduct);
    const isAddOfferingFlow = isUnsavedOffering || props.createNew;

    const allowedStrategyTypeIds = React.useMemo(
        () =>
            getAllowedDateStrategyTypeIds(
                (props.selectedProduct as any)?.type,
                props.selectedProduct as any
            ),
        [props.selectedProduct]
    );
    const strategyTypeOptions = React.useMemo(
        () => toStrategyTypeSelectOptions(allowedStrategyTypeIds),
        [allowedStrategyTypeIds]
    );
    const canChangeStrategyType =
        isUnsavedOffering && !props.createNew && allowedStrategyTypeIds.length > 1;

    const todayIso = DateHelper.ToDateString(new Date(), DateHelper.YYYYMMDD);

    const originalFromIsoForLock = selectedDateStrategy
        ? toIso(selectedDateStrategy.fromDate) || toIso(selectedDateStrategy.originalFromDate)
        : "";

    const isFromDateReadOnlyOnEdit = Boolean(
        !isUnsavedOffering &&
            !props.createNew &&
            !selectedDateStrategy?.isNew &&
            originalFromIsoForLock &&
            !DateHelper.CompareDates(originalFromIsoForLock, todayIso, CompareTypes.BiggerThan)
    );

    const currentFromIso = selectedDateStrategy
        ? toIso(selectedDateStrategy.fromDate) || toIso(selectedDateStrategy.originalFromDate)
        : "";

    const fromDateInputMin =
        props.createNew || isUnsavedOffering || selectedDateStrategy?.isNew === true
            ? minIsoDate(props.minDateFrom, currentFromIso || props.minDateFrom)
            : isFromDateReadOnlyOnEdit
              ? props.minDateFrom
              : maxIsoDate(props.minDateFrom, todayIso);

    React.useEffect(() => {
        if (!props.dateStrategyToUpdateCreate) return;

        setSelectedDateStrategy(
            normalizeDates(cloneDeep(props.dateStrategyToUpdateCreate))
        );
    }, [
        props.dateStrategyToUpdateCreate?.strategyTypeId,
        props.dateStrategyToUpdateCreate?.fromDate,
        props.dateStrategyToUpdateCreate?.toDate,
        props.dateStrategyToUpdateCreate?.recurranceStartDays,
        props.dateStrategyToUpdateCreate?.specificStartDates,
        props.dateStrategyToUpdateCreate?.fixedDurationDates,
    ]);

    const strategyDateDisabled = (minIso: string, maxIso: string) => (date: Dayjs) =>
        date.isBefore(dayjs(minIso), "day") || date.isAfter(dayjs(maxIso), "day");

    const dateStrategyDateChange = (value: Dayjs | null, isDateFrom: boolean) => {
        if (!selectedDateStrategy) return;

        const next = normalizeDates(cloneDeep(selectedDateStrategy));

        if (isDateFrom) {
            if (isFromDateReadOnlyOnEdit) {
                return;
            }

            if (!value) return;

            const currentTo = next.toDate ? toIso(next.toDate) : "";
            const fromMaxDateBase =
                currentTo && selectedDateStrategyType !== "Fixed Duration"
                    ? minIsoDate(currentTo, props.maxDateFrom)
                    : props.maxDateFrom;
            const fromMaxDate =
                isUnsavedOffering || props.createNew
                    ? maxIsoDate(fromMaxDateBase, currentFromIso || fromMaxDateBase)
                    : fromMaxDateBase;

            const normalized = shiftPickerDateToEarliestAllowed(
                value,
                fromDateInputMin,
                fromMaxDate
            );

            if (!normalized || isNaN(Date.parse(normalized))) return;

            next.fromDate = normalized as any;

            const nextTo = next.toDate ? toIso(next.toDate) : null;
            if (nextTo && DateHelper.CompareDates(nextTo, normalized, CompareTypes.SmallerThan)) {
                next.toDate = normalized as any;
            }

            setSelectedDateStrategy(next);
            props.onDateStrategyUpdate(next);
            props.onPendingChanges(hasPendingStrategyData(next));
            return;
        }

        if (!value) {
            next.toDate = null as any;
            setSelectedDateStrategy(next);
            props.onDateStrategyUpdate(next);
            props.onPendingChanges(hasPendingStrategyData(next));
            return;
        }

        const fromIso = toIso(next.fromDate) || props.minDateFrom;
        const normalized = shiftPickerDateToEarliestAllowed(value, fromIso, props.maxDateFrom);

        if (!normalized || isNaN(Date.parse(normalized))) return;
        if (DateHelper.CompareDates(normalized, fromIso, CompareTypes.SmallerThan)) return;

        next.toDate = normalized as any;
        setSelectedDateStrategy(next);
        props.onDateStrategyUpdate(next);
        props.onPendingChanges(hasPendingStrategyData(next));
    };

    const generateDatesEditSection = () => {
        if (!selectedDateStrategy) return null;

        if (
            selectedDateStrategyType === "Fixed Duration" ||
            selectedDateStrategyType === "Specific Start Dates"
        ) {
            return null;
        }

        const from = toIso(selectedDateStrategy.fromDate);
        const to = selectedDateStrategy.toDate ? toIso(selectedDateStrategy.toDate) : "";

        const fromMaxDateBase =
            to && selectedDateStrategyType !== "Fixed Duration"
                ? minIsoDate(to, props.maxDateFrom)
                : props.maxDateFrom;
        const fromMaxDate =
            isUnsavedOffering || props.createNew
                ? maxIsoDate(fromMaxDateBase, from || fromMaxDateBase)
                : fromMaxDateBase;

        const toMinDate = from || props.minDateFrom;
        const toMaxDate = props.maxDateFrom;

        return (
            <div className="date-strategy-applies-fields">
                <div className="date-strategy-applies-field">
                    <label htmlFor="date-strategy-applies-from">Applies From</label>
                    <DatePicker
                        id="date-strategy-applies-from"
                        format={DateHelper.DATE_PICKER_FORMATS}
                        placeholder="Select date"
                        disabled={
                            (currentStrategyTypeId === DateStrategy["Fixed Duration"] &&
                                !isAddOfferingFlow) ||
                            isFromDateReadOnlyOnEdit
                        }
                        value={from ? dayjs(from) : null}
                        disabledDate={strategyDateDisabled(fromDateInputMin, fromMaxDate)}
                        onChange={(value) => dateStrategyDateChange(value, true)}
                        style={{ width: 200 }}
                    />
                </div>
                <div className="date-strategy-applies-field">
                    <label htmlFor="date-strategy-applies-to">Applies To</label>
                    <DatePicker
                        id="date-strategy-applies-to"
                        format={DateHelper.DATE_PICKER_FORMATS}
                        placeholder="Select date"
                        disabled={currentStrategyTypeId === DateStrategy["Fixed Duration"]}
                        value={to ? dayjs(to) : null}
                        disabledDate={strategyDateDisabled(toMinDate, toMaxDate)}
                        onChange={(value) => dateStrategyDateChange(value, false)}
                        style={{ width: 200 }}
                    />
                </div>
            </div>
        );
    };

    const changeStrategyType = (nextTypeId: number) => {
        if (!selectedDateStrategy || selectedDateStrategy.strategyTypeId === nextTypeId) {
            return;
        }

        const next = switchStrategyType(
            selectedDateStrategy,
            nextTypeId,
            props.allowedEndDate
        );
        setSelectedDateStrategy(next);
        props.onDateStrategyUpdate(next);
        props.onPendingChanges(hasPendingStrategyData(next));
    };

    const generateStrategyTypeHeader = () => {
        const tooltip = STRATEGY_TOOLTIPS[selectedDateStrategyType];

        if (canChangeStrategyType) {
            return (
                <div className="date-strategy-type-header date-strategy-type-header--editable">
                    <label htmlFor="date-strategy-type-select">Strategy Type</label>
                    <Select
                        id="date-strategy-type-select"
                        aria-label="Date strategy type"
                        value={currentStrategyTypeId}
                        options={strategyTypeOptions}
                        onChange={(value) => changeStrategyType(Number(value))}
                        style={{ minWidth: 240 }}
                    />
                    {tooltip ? (
                        <Tooltip title={tooltip}>
                            <InfoCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
                        </Tooltip>
                    ) : null}
                </div>
            );
        }

        if (!tooltip) return null;

        return (
            <div className="date-strategy-type-header">
                <span className="date-strategy-type-name">{selectedDateStrategyType}</span>
                <Tooltip title={tooltip}>
                    <InfoCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
                </Tooltip>
            </div>
        );
    };

    const appliesFields = generateDatesEditSection();

    return (
        <div className="container">
            <div className="content">
                {appliesFields ? (
                    <Row>
                        <Col span={24}>{appliesFields}</Col>
                    </Row>
                ) : null}

                        <Row>
                            <Col span={24}>{generateStrategyTypeHeader()}</Col>
                        </Row>

                    {selectedDateStrategyType !== "Offering Dates" && selectedDateStrategy ? (
                        <Row>
                            <Col span={24}>
                                <DateStrategyContent
                                    key={currentStrategyTypeId}
                                    createNew={props.createNew}
                                    isAddOfferingFlow={isAddOfferingFlow}
                                    allowedStartDate={props.allowedStartDate}
                                    allowedEndDate={props.allowedEndDate}
                                    dateStrategyToUpdateCreate={selectedDateStrategy}
                                    productUnitTypeId={(props.selectedProduct as any)?.unitTypeId}
                                    productUnitTypeName={
                                        (props.selectedProduct as any)?.unitTypeName ??
                                        (props.selectedProduct as any)?.unitTypeText
                                    }
                                    onPendingChanges={props.onPendingChanges}
                                    onDateStrategyUpdate={(updatedStrategy) => {
                                        const normalized = normalizeDates(cloneDeep(updatedStrategy));
                                        setSelectedDateStrategy(normalized);
                                        props.onDateStrategyUpdate(normalized);
                                    }}
                                />
                            </Col>
                        </Row>
                    ) : null}
            </div>
        </div>
    );
}

const mapStateToProps = (state: any): StateProps => ({
    pricingYears: getPricingYears(state),
    selectedPricingYear: getSelectedPricingYear(state),
    user: getCurrentUser(state),
});

const mapDispatchToProps = (_dispatch: ThunkDispatch<any, any, any>): DispatchProps => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DateStrategyModalView);